import { makeAutoObservable, runInAction } from "mobx"
import type { ComputerBackend, ComputerSummary } from "../../computers/types"
import { connectNestConnection, createNestClient, type NestClient } from "../../nest/client"
import type { RuntimeRecord } from "../../nest/generated"

export interface ProcessComputer {
    computer: ComputerSummary
    processes: RuntimeRecord[]
    status: "waiting" | "ready" | "error"
    cursor?: string
    nextCursor?: string
    error?: string
}

export interface ProcessRefreshBatch {
    status: "refreshing" | "done" | "done-with-gaps"
    requested: number
    succeeded: number
    failed: number
    firstErrors: Array<{ nodeId: string; message: string }>
}

// Exists only while the global Processes surface is open. Nest owns the records;
// this model owns bounded pages and temporary, explicitly targeted connections.
export class ProcessFleetModel {
    computers: ProcessComputer[]
    status: "active" | "all" = "active"
    namespace = ""
    batch: ProcessRefreshBatch | null = null
    private readonly clients = new Map<string, NestClient>()
    private readonly controller = new AbortController()
    private pending: Promise<void> | null = null
    private timer: ReturnType<typeof setTimeout> | undefined
    private revision = 0
    private refreshRequested = false

    constructor(
        private readonly backend: ComputerBackend,
        computers: ComputerSummary[]
    ) {
        this.computers = computers.map((computer) => ({ computer, processes: [], status: "waiting" }))
        makeAutoObservable<ProcessFleetModel, "backend" | "clients" | "controller" | "pending" | "timer" | "revision" | "refreshRequested">(this, {
            backend: false,
            clients: false,
            controller: false,
            pending: false,
            timer: false,
            revision: false,
            refreshRequested: false,
        })
    }

    get loading(): boolean {
        return this.batch?.status === "refreshing"
    }

    clientFor(nodeId: string): NestClient | null {
        return this.clients.get(nodeId) ?? null
    }

    setFilters(status: "active" | "all", namespace: string): void {
        this.status = status
        this.namespace = namespace
        for (const entry of this.computers) {
            entry.cursor = undefined
            entry.nextCursor = undefined
            entry.processes = []
        }
        this.revision += 1
        void this.refresh()
    }

    setPage(nodeId: string, cursor?: string): void {
        const entry = this.computers.find((entry) => entry.computer.nodeId === nodeId)
        if (!entry) return
        entry.cursor = cursor
        entry.nextCursor = undefined
        entry.processes = []
        this.revision += 1
        void this.refresh()
    }

    refresh(): Promise<void> {
        if (this.controller.signal.aborted) return Promise.resolve()
        clearTimeout(this.timer)
        this.refreshRequested = true
        // Filter changes and manual refreshes coalesce behind the current batch.
        // There can never be two independent fleet polling loops.
        if (this.pending) return this.pending
        this.pending = this.refreshLoop().finally(() => {
            this.pending = null
            if (!this.controller.signal.aborted) this.timer = setTimeout(() => void this.refresh(), 2_000)
        })
        return this.pending
    }

    dispose(): void {
        clearTimeout(this.timer)
        this.controller.abort()
        for (const client of this.clients.values()) client.close()
        this.clients.clear()
    }

    private async refreshLoop(): Promise<void> {
        while (this.refreshRequested && !this.controller.signal.aborted) {
            this.refreshRequested = false
            await this.refreshBatch()
        }
    }

    private async refreshBatch(): Promise<void> {
        const revision = this.revision
        const status = this.status === "active" ? "active" : undefined
        const namespace = this.namespace || undefined
        const entries = this.computers.map((entry) => ({ entry, cursor: entry.cursor }))
        const batch: ProcessRefreshBatch = { status: "refreshing", requested: entries.length, succeeded: 0, failed: 0, firstErrors: [] }
        runInAction(() => {
            this.batch = { ...batch }
        })
        let nextIndex = 0
        const worker = async () => {
            for (;;) {
                const next = entries[nextIndex++]
                if (!next || this.controller.signal.aborted || revision !== this.revision) return
                const { entry, cursor } = next
                const nodeId = entry.computer.nodeId
                try {
                    const client = await this.connect(nodeId)
                    const result = await client.requestMethod(
                        "process.list",
                        { status, namespace, cursor, limit: 100 },
                        {
                            signal: this.controller.signal,
                            timeoutMs: 15_000,
                        }
                    )
                    if (this.controller.signal.aborted || revision !== this.revision) return
                    runInAction(() => {
                        entry.processes = result.processes
                        entry.nextCursor = result.nextCursor
                        entry.status = "ready"
                        entry.error = undefined
                        batch.succeeded += 1
                        this.batch = { ...batch, firstErrors: [...batch.firstErrors] }
                    })
                } catch (error) {
                    this.clients.get(nodeId)?.close()
                    this.clients.delete(nodeId)
                    if (this.controller.signal.aborted || revision !== this.revision) return
                    const message = error instanceof Error ? error.message : String(error)
                    runInAction(() => {
                        entry.status = "error"
                        entry.error = message
                        batch.failed += 1
                        if (batch.firstErrors.length < 5) batch.firstErrors.push({ nodeId, message })
                        this.batch = { ...batch, firstErrors: [...batch.firstErrors] }
                    })
                }
            }
        }
        await Promise.all(Array.from({ length: Math.min(3, entries.length) }, () => worker()))
        if (!this.controller.signal.aborted && revision === this.revision)
            runInAction(() => {
                this.batch = { ...batch, status: batch.failed ? "done-with-gaps" : "done", firstErrors: [...batch.firstErrors] }
            })
    }

    private async connect(nodeId: string): Promise<NestClient> {
        const existing = this.clients.get(nodeId)
        if (existing) return existing
        const client = createNestClient()
        this.clients.set(nodeId, client)
        const connection = await this.backend.getConnection(nodeId)
        this.controller.signal.throwIfAborted()
        await connectNestConnection(client, connection, { timeoutMs: 6_000 })
        this.controller.signal.throwIfAborted()
        return client
    }
}
